import React, { useEffect, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Alert,
} from "reactstrap"
import { useDispatch } from "react-redux"
import { addNewUser as onAddNewUser } from "store/actions"
import { getParceiros } from "helpers/parceiro_helper"

const PERFIS_POR_ROLE = {
  super_admin: [
    { value: "admin", label: "Admin" },
    { value: "analista", label: "Analista" },
    { value: "cliente", label: "Cliente" },
  ],
  admin: [
    { value: "analista", label: "Analista" },
    { value: "cliente", label: "Cliente" },
  ],
}

const ModalAddUser = ({
  toggleModal,
  showModal,
  inputs,
  setInputs,
  isValidInputs,
  setIsValidInputs,
  loading,
  setLoading,
  error,
}) => {
  const dispatch = useDispatch()
  const [parceiros, setParceiros] = useState([])

  const authUser = JSON.parse(window.sessionStorage.getItem("authUser") || "{}")
  const perfilLogado = authUser?.perfil
  const perfilOptions = PERFIS_POR_ROLE[perfilLogado] || []

  const isCliente = inputs[0]?.perfil === "cliente"

  useEffect(() => {
    if (showModal && parceiros.length === 0) {
      getParceiros()
        .then(data => {
          const ativos = (Array.isArray(data) ? data : []).filter(p => p.Status !== false)
          setParceiros(ativos)
        })
        .catch(() => setParceiros([]))
    }
  }, [showModal])

  const seguradoras = parceiros.filter(p => p.Tipo === "seguradora")
  const corretoras = parceiros.filter(p => p.Tipo === "corretora")

  const handleInputChange = (index, event) => {
    const { name, value } = event.target
    const newInputs = [...inputs]
    newInputs[index] = { ...newInputs[index], [name]: value }
    setInputs(newInputs)
    validateInputs(index, newInputs[index])
  }

  const handleSubmit = () => {
    const user = inputs[0]
    setLoading(true)
    dispatch(onAddNewUser({
      Name: user.nome,
      Email: user.email,
      Password: user.senha,
      Perfil: user.perfil,
      ParceiroId: user.perfil === "cliente" ? user.parceiroId : undefined,
    }))
  }

  const validateEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  const validateInputs = (index, input) => {
    const newIsValidInputs = [...isValidInputs]
    newIsValidInputs[index] = {
      nome: input.nome.trim().length >= 3 && input.nome.trim().length <= 250,
      email: validateEmail(input.email),
      senha: input.senha.length >= 6,
      perfil: !!input.perfil,
      parceiroId: input.perfil === "cliente" ? !!input.parceiroId : true,
    }
    setIsValidInputs(newIsValidInputs)
  }

  return (
    <Modal isOpen={showModal} toggle={toggleModal}>
      <ModalHeader toggle={toggleModal}>Cadastrar novo usuário</ModalHeader>

      {error && <Alert color="danger" className="mb-0 rounded-0">{error}</Alert>}

      <ModalBody>
        {inputs.map((input, index) => (
          <div key={index}>
            <Form autoComplete="off">
              <div style={{ height: 0, overflow: "hidden" }}>
                <input type="text" name="username" autoComplete="username" tabIndex="-1" />
                <input type="password" name="password" autoComplete="current-password" tabIndex="-1" />
              </div>

              <FormGroup>
                <Label for={`nome_${index}`}>Nome *</Label>
                <Input
                  type="text"
                  name="nome"
                  id={`nome_${index}`}
                  placeholder="Nome completo"
                  value={input.nome}
                  onChange={event => handleInputChange(index, event)}
                  valid={isValidInputs[index]?.nome}
                  invalid={input.nome.length > 0 && !isValidInputs[index]?.nome}
                  autoComplete="off"
                />
                <FormFeedback invalid>O nome deve ter entre 3 e 250 caracteres.</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label for={`email_${index}`}>E-mail *</Label>
                <Input
                  type="email"
                  name="email"
                  id={`email_${index}`}
                  placeholder="email@exemplo.com"
                  value={input.email}
                  onChange={event => handleInputChange(index, event)}
                  valid={isValidInputs[index]?.email}
                  invalid={input.email.length > 0 && !isValidInputs[index]?.email}
                  autoComplete="off"
                />
                <FormFeedback invalid>Insira um e-mail válido.</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label for={`perfil_${index}`}>Perfil *</Label>
                <Input
                  type="select"
                  name="perfil"
                  id={`perfil_${index}`}
                  value={input.perfil}
                  onChange={event => handleInputChange(index, event)}
                  valid={isValidInputs[index]?.perfil}
                  invalid={input.perfil !== "" && !isValidInputs[index]?.perfil}
                >
                  <option value="">Selecione...</option>
                  {perfilOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Input>
                <FormFeedback invalid>Selecione um perfil.</FormFeedback>
              </FormGroup>

              {isCliente && (
                <FormGroup>
                  <Label for={`parceiroId_${index}`}>Parceiro *</Label>
                  <Input
                    type="select"
                    name="parceiroId"
                    id={`parceiroId_${index}`}
                    value={input.parceiroId}
                    onChange={event => handleInputChange(index, event)}
                    valid={isValidInputs[index]?.parceiroId}
                    invalid={!isValidInputs[index]?.parceiroId && input.parceiroId === ""}
                  >
                    <option value="">Selecione um parceiro...</option>
                    {seguradoras.length > 0 && (
                      <optgroup label="Seguradoras">
                        {seguradoras.map(p => (
                          <option key={p.Id} value={p.Id}>{p.Nome}</option>
                        ))}
                      </optgroup>
                    )}
                    {corretoras.length > 0 && (
                      <optgroup label="Corretoras">
                        {corretoras.map(p => (
                          <option key={p.Id} value={p.Id}>{p.Nome}</option>
                        ))}
                      </optgroup>
                    )}
                  </Input>
                  <FormFeedback invalid>Selecione um parceiro.</FormFeedback>
                </FormGroup>
              )}

              <FormGroup>
                <Label for={`senha_${index}`}>Senha *</Label>
                <Input
                  type="password"
                  name="senha"
                  id={`senha_${index}`}
                  placeholder="Mínimo 6 caracteres"
                  value={input.senha}
                  onChange={event => handleInputChange(index, event)}
                  valid={isValidInputs[index]?.senha}
                  invalid={input.senha.length > 0 && !isValidInputs[index]?.senha}
                  autoComplete="new-password"
                />
                <FormFeedback invalid>A senha deve ter pelo menos 6 caracteres.</FormFeedback>
              </FormGroup>
            </Form>
          </div>
        ))}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggleModal}>
          Cancelar
        </Button>
        <Button
          color="success"
          onClick={!loading ? handleSubmit : undefined}
          disabled={
            loading ||
            !isValidInputs.every(v => v.nome && v.email && v.senha && v.perfil && v.parceiroId)
          }
        >
          <i className={loading ? "bx bx-loader bx-spin align-middle me-2" : ""} />
          {loading ? "Cadastrando..." : "Cadastrar"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ModalAddUser
